// src/app/klipAcmaKarari.ts
//
// KLİP AÇMA KARARI — "bu klip hangi yüzeyde açılır?" sorusunun TEK saf yanıtı.
//
// ── ÖLÇÜLEN KUSUR (denetim §3.2 / P0-1, 2026-09-03) ─────────────────────────
// Üründe İKİ klip yüzeyi vardı ve kullanıcı aynı klibi iki farklı şekilde
// görüyordu:
//   · MyClips sekmesi → V3 tam-ekran (pager, ses, sağ ray, 5★, yorum)
//   · akış kartı / paylaşılan `/c/{id}` → 2025 kalıntısı `ClipDetailModal*`
//     (mobilde YALNIZ ✕ + video; ★ yok, yorum yazma yok, paylaş yok, kaydet yok)
// Yani paylaşılan HER bağlantı ve akıştaki HER klip dokunuşu ZAYIF yüzeye
// düşüyordu. Bu modül o yönlendirmeyi karara bağlar — oynatma/pager/poster
// dosyalarına DOKUNMADAN (kanon: «KORUNMASI ZORUNLU DEĞİŞMEZLER», 15 madde).
//
// ── NEDEN AYRI, SAF BİR MODÜL ───────────────────────────────────────────────
// Karar İKİ çağrı yerinde lazım (`App.handleViewClip` ve `appRouting`in
// `/c/{id}` çözücüsü) ve ikisi de yan-etkili. Kararı orada tekrar yazmak,
// "V3 klip nedir" tanımının İKİ kopyasını doğururdu — bu depoda tam olarak o
// sınıf birden çok kez kusur üretti (bkz. `klipYildizOtoritesi`, `klipAlanlari`).
// `klipV3Mu` bu yüzden TEK OTORİTEDİR: `appRouting.openClipFromUrl` içindeki
// satır-içi üçlü koşul kaldırılıp buraya taşındı.

/**
 * Karar için OKUNAN alanlar. Şekil UYDURULMADI: `appRouting.openClipFromUrl`
 * gövdesindeki üçlü koşulun (`v3_uploadedVia` · `status` · `v3_r2Url`) ve
 * `pendingV3Anchor`ın (`clipId` · `scopedUid`) okuduğu alanların birleşimi.
 * Alanlar `unknown`: çağıranların biri Firestore ham dokümanı, öteki akış
 * öğesi geçiyor — ikisi de serbest şekilli.
 */
export type KlipKaydi =
  | {
      id?: unknown;
      authorId?: unknown;
      uid?: unknown;
      v3_uploadedVia?: unknown;
      status?: unknown;
      v3_r2Url?: unknown;
    }
  | null
  | undefined;

/**
 * V3 klip mi? TEK TANIM.
 *
 * ⚠️ Üç kolun üçü de gerekli ve hiçbiri diğerinin yerine geçmez:
 *   · `v3_uploadedVia === "v3"` — V3 yükleyicisinin yazdığı damga
 *   · `status === "v3_only"`    — V2'den taşınmış, yalnız V3'te oynayan kayıt
 *   · `v3_r2Url`                — damgayı taşımayan ama R2/HLS medyası olan kayıt
 * V3 OLMAYAN (v1/v2) klipler legacy modalda kalır: V3 feed'i onları
 * oynatamaz (`clipsRepo` şekli yok) → yönlendirmek siyah ekran üretirdi.
 */
export function klipV3Mu(klip: KlipKaydi): boolean {
  if (!klip) return false;
  return (
    klip.v3_uploadedVia === "v3" ||
    klip.status === "v3_only" ||
    !!klip.v3_r2Url
  );
}

/** Boş olmayan metin ya da `null` (çağıranların `x || null` deseninin tipi). */
function metin(v: unknown): string | null {
  return typeof v === "string" && v ? v : null;
}

/**
 * Klibin yazarı — `pendingV3Anchor.scopedUid`e giden değer. `authorId` yoksa
 * `uid` (eski kliplerde alan adı buydu); `openClipFromUrl`ün zaten kullandığı
 * yedek zincirin BİREBİR aynısı.
 */
export function klipYazariUid(klip: KlipKaydi): string | null {
  if (!klip) return null;
  return metin(klip.authorId) || metin(klip.uid);
}

export type KlipAcmaKarari =
  | {
      hedef: "v3feed";
      clipId: string;
      /** `pendingV3Anchor.scopedUid` — feed bu yazarın kliplerini sayfalar. */
      scopedUid: string | null;
    }
  | {
      hedef: "legacy";
      /**
       * Neden V3'e gitmedi. Üç hâl ayrı tutulur (kanon: TEMİZ · İHLAL ·
       * ÖLÇEMEDİM'in karar hâli) — "legacy" tek bir kova olsaydı, ileride
       * "id yok" ile "v2 klip" aynı şeymiş gibi görünürdü.
       */
      sebep: "id-yok" | "v3-degil" | "akis-zaten-acik";
    };

/**
 * AKIŞ/KEŞFET kartından klip açma kararı (`App.handleViewClip`).
 *
 * ⚠️ `aktifSayfa === "clips"` → LEGACY. Sebebi ölçülmüş bir mekanizma
 * kısıtıdır, tercih değil: bu yol klibi `setPendingV3Anchor` + sayfa geçişiyle
 * devrediyor ve pending YALNIZ `FeedContainerV3` MOUNT'ında tüketiliyor
 * (`FeedContainerV3.tsx` → `consumePendingV3Anchor`). Feed zaten mount'luysa
 * `setActivePage("clips")` bir no-op'tur → pending TÜKETİLMEZ, ekranda hiçbir
 * şey olmaz ve bayat bir pending sonraki mount'ta YANLIŞ klibi açar.
 * Kilitli dosyaya dokunmadan bunu çözmenin yolu olayla anchor'lamaktır, o da
 * `scopedClips` (yani `clipsRepo.mapPlainClipsToPlayable`) ister —
 * `appRouting.openClipFromUrl` o modülü ZATEN dinamik yüklüyor, `App` ise
 * yüklemiyor (ölçülmüş ~50 kB ana-paket bedeli, #1019).
 *
 * 📏 Bu dal ÜRÜNDE ULAŞILAMAZ: `handleViewClip` yalnız `home`, `explore` ve
 * `default` sayfalarına bağlı (`app/appPages.tsx`), `clips`e DEĞİL. Yine de
 * savunma olarak yazıldı — çağrı yerinin ileride değişmesi sessiz bir kayıp
 * değil, ölçülebilir bir "legacy" kararı üretsin.
 */
export function akisKlibiAcmaKarari(
  klip: KlipKaydi,
  aktifSayfa?: string | null,
): KlipAcmaKarari {
  const clipId = metin(klip?.id);
  if (!clipId) return { hedef: "legacy", sebep: "id-yok" };
  if (!klipV3Mu(klip)) return { hedef: "legacy", sebep: "v3-degil" };
  if (aktifSayfa === "clips") return { hedef: "legacy", sebep: "akis-zaten-acik" };
  return { hedef: "v3feed", clipId, scopedUid: klipYazariUid(klip) };
}
